/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useCallback, useEffect, useState, type CSSProperties } from 'react';

import { cssMap, keyframes, jsx } from '@compiled/react';
import { createPortal } from 'react-dom';

import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

const LIGHT_COLORS = ['#FCA700', '#6A9A23', '#1868DB', '#AF59E0'];
const DARK_COLORS = ['#FCA700', '#82B536', '#357DE8', '#BF63F3'];

const svgStrokeAnimation = keyframes({
	from: {
		strokeDashoffset: 'var(--stroke-perimeter)',
	},
	to: {
		strokeDashoffset: 0,
	},
});

const styles = cssMap({
	overlay: {
		position: 'absolute',
		insetBlockEnd: 0,
		insetBlockStart: 0,
		insetInlineEnd: 0,
		insetInlineStart: 0,
		pointerEvents: 'none',
		animation: `${svgStrokeAnimation} 1.5s linear infinite`,
		strokeOpacity: 1,
	},
	svg: {
		position: 'absolute',
		insetBlockStart: 0,
		insetInlineStart: 0,
		fill: 'transparent',
	},
});

type AnimatedGradientBorderProps = {
	borderRadius?: boolean;
	containerElement: HTMLElement | null;
	isExecuting?: boolean;
};

type StrokePerimeterStyle = CSSProperties & {
	'--stroke-perimeter': number;
};

type BorderSize = {
	height: number;
	perimeter: number;
	width: number;
};

const emptyBorderSize: BorderSize = {
	height: 0,
	perimeter: 0,
	width: 0,
};

const getBorderSize = (element: HTMLElement): BorderSize => {
	const rect = element.getBoundingClientRect();
	const height = Math.round(Math.max(rect.height, element.scrollHeight));
	const width = Math.round(Math.max(rect.width, element.scrollWidth));

	return {
		height,
		perimeter: (height + width) * 2,
		width,
	};
};

export const AnimatedGradientBorder = ({
	borderRadius = true,
	containerElement,
	isExecuting = false,
}: AnimatedGradientBorderProps): JSX.Element | null => {
	const { colorMode } = useThemeObserver();
	const colors = colorMode === 'dark' ? DARK_COLORS : LIGHT_COLORS;
	const [borderSize, setBorderSize] = useState<BorderSize>(emptyBorderSize);

	useEffect(() => {
		if (!containerElement || !isExecuting) {
			setBorderSize(emptyBorderSize);
			return;
		}

		if (typeof window === 'undefined') {
			return;
		}

		let rafId: number | undefined;

		const updateBorderSize = () => {
			if (rafId !== undefined) {
				cancelAnimationFrame(rafId);
			}

			rafId = requestAnimationFrame(() => {
				setBorderSize(getBorderSize(containerElement));
				rafId = undefined;
			});
		};

		updateBorderSize();

		if (typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', updateBorderSize);

			return () => {
				if (rafId !== undefined) {
					cancelAnimationFrame(rafId);
				}
				window.removeEventListener('resize', updateBorderSize);
			};
		}

		const resizeObserver = new ResizeObserver(updateBorderSize);
		resizeObserver.observe(containerElement);

		return () => {
			if (rafId !== undefined) {
				cancelAnimationFrame(rafId);
			}
			resizeObserver.disconnect();
		};
	}, [containerElement, isExecuting]);

	const calculateDashArray = useCallback(
		(renderPosition: number) => {
			if (renderPosition === 0) {
				return 0;
			}
			const dashArrayLength = Math.round(
				(borderSize.perimeter / colors.length) * (colors.length - renderPosition),
			);
			const dashOffset = borderSize.perimeter - dashArrayLength;
			return `${dashArrayLength} ${dashOffset}`;
		},
		[borderSize.perimeter, colors],
	);

	if (!containerElement || !isExecuting || borderSize.height === 0 || borderSize.width === 0) {
		return null;
	}

	const overlayStyle: StrokePerimeterStyle = {
		'--stroke-perimeter': borderSize.perimeter,
		height: borderSize.height,
		width: borderSize.width,
	};
	const rectHeight = Math.max(borderSize.height - 2, 0);
	const rectWidth = Math.max(borderSize.width - 2, 0);

	return createPortal(
		<div
			css={styles.overlay}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={overlayStyle}
		>
			{colors.map((color, index) => (
				<Fragment key={color}>
					<svg
						css={styles.svg}
						height={borderSize.height}
						width={borderSize.width}
						xmlns="http://www.w3.org/2000/svg"
					>
						<rect
							data-testid={`animated-${color}`}
							height={rectHeight}
							rx={borderRadius ? '4' : '0'}
							ry={borderRadius ? '4' : '0'}
							stroke={color}
							strokeDasharray={calculateDashArray(index)}
							strokeWidth={token('space.025')}
							width={rectWidth}
							x="1"
							y="1"
						/>
					</svg>
				</Fragment>
			))}
		</div>,
		containerElement,
	);
};
